import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { useTranslation } from "react-i18next";
import {
  getFusionResultLabel,
  getFusionResultTone,
} from "lib/utils/fusionResult";

const TEMPLATE_URL = `${
  process.env.PUBLIC_URL || ""
}/templates/ud1000-video-template.xlsx`;

const TONE_FILL_BY_RESULT = {
  normal: {
    title: "d9d9d9",
    label: "f2f2f2",
  },
  warning: {
    title: "d8bee4",
    label: "ebdef1",
  },
  abnormal: {
    title: "ffceb0",
    label: "ffe7d8",
  },
  recheck: {
    title: "cbdd61",
    label: "e3eca6",
  },
};

const TONE_LABEL_CELLS = [
  "A3",
  "A4",
  "A5",
  "A6",
  "A7",
  "A8",
  "A12",
  "C3",
  "C4",
  "C5",
  "C6",
  "E4",
  "E5",
  "E6",
  "E8",
  "E10",
  "E12",
  "E14",
  "G3",
  "G4",
  "G5",
  "G6",
];

function setCellValue(sheet, address, value) {
  sheet.getCell(address).value = value ?? "";
}

function mergeCellAlignment(sheet, address, alignment) {
  const cell = sheet.getCell(address);
  cell.alignment = {
    ...(cell.alignment ?? {}),
    ...alignment,
  };
}

function mergeCellFont(sheet, address, font) {
  const cell = sheet.getCell(address);
  cell.font = {
    ...(cell.font ?? {}),
    ...font,
  };
}

function setCellFill(sheet, address, fill) {
  sheet.getCell(address).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: fill },
  };
}

function getBase64ImageExtension(base64) {
  const match = /^data:image\/([a-zA-Z0-9+.-]+);base64,/.exec(base64 ?? "");
  const extension = match?.[1]?.toLowerCase();

  if (extension === "jpg") return "jpeg";
  if (extension === "svg+xml") return "png";
  return extension || "png";
}

function addBase64Image(workbook, sheet, base64, range) {
  if (!base64 || typeof base64 !== "string") return;

  const imageId = workbook.addImage({
    base64,
    extension: getBase64ImageExtension(base64),
  });

  sheet.addImage(imageId, range);
}

export async function loadUdVideoExcelTemplateWorkbook() {
  const response = await fetch(TEMPLATE_URL);

  if (!response.ok) {
    throw new Error(`template fetch failed: ${response.status}`);
  }

  const arrayBuffer = await response.arrayBuffer();
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(arrayBuffer);

  return workbook;
}

export function populateUdVideoExcelWorksheet({
  workbook,
  sheet,
  form,
  images,
  t,
  i18n,
}) {
  if (!workbook || !sheet) {
    throw new Error("populateUdVideoExcelWorksheet requires workbook and sheet");
  }

  sheet.views = [
    {
      state: "normal",
      style: "pageBreakPreview",
      activeCell: "A1",
      zoomScale: 100,
      zoomScaleNormal: 100,
    },
  ];

  const fusionTone =
    getFusionResultTone(form?.fusion_result_code ?? form?.fusion_result) ??
    "normal";
  const toneFill =
    TONE_FILL_BY_RESULT[fusionTone] ?? TONE_FILL_BY_RESULT.normal;
  const fusionResultLabel = getFusionResultLabel(
    form?.fusion_result_code ?? form?.fusion_result,
    i18n,
    {
      variant: "video",
      fallback: form?.fusion_result ?? "",
    },
  );

  TONE_LABEL_CELLS.forEach((address) => {
    setCellFill(sheet, address, toneFill.label);
    mergeCellFont(sheet, address, { bold: true, size: 9 });
  });

  setCellFill(sheet, "A1", toneFill.title);
  mergeCellFont(sheet, "A1", { size: 16 });
  mergeCellFont(sheet, "D5", { size: 8 });
  mergeCellFont(sheet, "B5", { size: 9 });
  mergeCellFont(sheet, "B6", { size: 9 });
  mergeCellFont(sheet, "B7", { size: 9 });
  mergeCellAlignment(sheet, "B7", {
    horizontal: "left",
    vertical: "middle",
    wrapText: true,
  });
  mergeCellAlignment(sheet, "D5", {
    horizontal: "center",
    vertical: "middle",
    wrapText: true,
  });

  setCellValue(sheet, "A1", t("videoFileIndex.report.title"));
  setCellValue(sheet, "A2", "");
  setCellValue(sheet, "A3", t("videoFileIndex.report.facilityType"));
  setCellValue(sheet, "A4", t("videoFileIndex.report.buildingName"));
  setCellValue(sheet, "A5", `${t("videoFileIndex.report.ultrasonicIntensity")}\n(dB)`);
  setCellValue(sheet, "A6", `${t("videoFileIndex.report.measurementDistance")}\n(m)`);
  setCellValue(
    sheet,
    "A7",
    `${t("videoFileIndex.report.inspectionResult.title")}\n${t("videoFileIndex.report.inspectionResult.label")}`,
  );
  setCellValue(
    sheet,
    "A8",
    `${t("videoFileIndex.report.ultrasonicDiagnosis")} (A) ${form?.time_1 ?? ""}`,
  );
  setCellValue(
    sheet,
    "A12",
    `${t("videoFileIndex.report.ultrasonicDiagnosis")} (B) ${form?.time_2 ?? ""}`,
  );

  setCellValue(sheet, "B3", form?.facility_type);
  setCellValue(sheet, "B4", form?.building_name);
  setCellValue(
    sheet,
    "B5",
    `A: ${form?.frame_decibel_1 ?? ""} / B: ${form?.frame_decibel_2 ?? ""}`,
  );
  setCellValue(
    sheet,
    "B6",
    `A: ${form?.frame_distance_1 ?? ""} / B: ${form?.frame_distance_2 ?? ""}`,
  );
  setCellValue(
    sheet,
    "B7",
    typeof form?.inspection_result === "string"
      ? form.inspection_result.replace(/^\n+/, "")
      : form?.inspection_result,
  );

  setCellValue(sheet, "C3", t("videoFileIndex.report.facilityName"));
  setCellValue(sheet, "C4", t("videoFileIndex.report.floor"));
  setCellValue(
    sheet,
    "C5",
    `${t("videoFileIndex.report.thermalTemperature")}\n(℃)`,
  );
  setCellValue(
    sheet,
    "C6",
    `${t("videoFileIndex.report.ambientTemperature")}\n(℃)`,
  );

  setCellValue(sheet, "D3", form?.facility_name);
  setCellValue(sheet, "D4", form?.floor);
  setCellValue(
    sheet,
    "D5",
    `${t("videoFileIndex.report.thermal.max")}: ${form?.thermal_max ?? ""}\n${t("videoFileIndex.report.thermal.min")}: ${form?.thermal_min ?? ""}\n${t("videoFileIndex.report.thermal.avg")}: ${form?.thermal_avg ?? ""}`,
  );
  setCellValue(sheet, "D6", form?.ambient_temp);

  setCellValue(sheet, "E4", t("videoFileIndex.report.location"));
  setCellValue(sheet, "E5", t("videoFileIndex.report.uvDetected"));
  setCellValue(sheet, "E6", t("videoFileIndex.report.equipmentUsed"));
  setCellValue(
    sheet,
    "E8",
    `${t("videoFileIndex.report.fastFourierTransform")} (A)`,
  );
  setCellValue(
    sheet,
    "E10",
    `${t("videoFileIndex.report.waveform")} (A)`,
  );
  setCellValue(
    sheet,
    "E12",
    `${t("videoFileIndex.report.fastFourierTransform")} (B)`,
  );
  setCellValue(
    sheet,
    "E14",
    `${t("videoFileIndex.report.waveform")} (B)`,
  );

  setCellValue(sheet, "F4", form?.location);
  setCellValue(sheet, "F5", "-");
  setCellValue(sheet, "F6", form?.equipment_used);

  setCellValue(sheet, "G3", t("videoFileIndex.report.partName"));
  setCellValue(sheet, "G4", t("videoFileIndex.report.shootingDate"));
  setCellValue(sheet, "G5", t("videoFileIndex.report.fusionResultMultiline"));
  setCellValue(sheet, "G6", t("videoFileIndex.report.serialNumber"));

  setCellValue(sheet, "H3", form?.part_name);
  setCellValue(sheet, "H4", form?.shooting_date);
  setCellValue(sheet, "H5", fusionResultLabel);
  setCellValue(sheet, "H6", form?.serial_number);

  addBase64Image(workbook, sheet, images?.frame_image_1, "A9:D11");
  addBase64Image(workbook, sheet, images?.graph_image_1, "E9:H9");
  addBase64Image(workbook, sheet, images?.waveform_image_1, "E11:H11");
  addBase64Image(workbook, sheet, images?.frame_image_2, "A13:D15");
  addBase64Image(workbook, sheet, images?.graph_image_2, "E13:H13");
  addBase64Image(workbook, sheet, images?.waveform_image_2, "E15:H15");
}

export default function VideoExcelDownloadBtn({ t, form, images }) {
  const { i18n } = useTranslation();

  const handleDownload = async () => {
    const workbook = await loadUdVideoExcelTemplateWorkbook();
    const sheet = workbook.worksheets[0];

    populateUdVideoExcelWorksheet({
      workbook,
      sheet,
      form,
      images,
      t,
      i18n,
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const file = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(file, `${form?.clean_file_name}.xlsx`);
  };

  return (
    <button className="btn-bg-blue" onClick={handleDownload}>
      {t("videoFileIndex.buttons.ExcelDownload")}
    </button>
  );
}
